/**
 * customTemplates — per-user reusable document templates.
 *
 * A med student can save the current document as a named template ("Save as
 * template" in the clinic Tools menu), keyed by a short TYPE CODE they choose.
 * Later they type that code into the AddPatientModal "Type" field; when the
 * patient's document is first opened (or "Reload template" is clicked) the
 * doc is seeded from that saved template instead of a built-in one.
 *
 * Templates are stored per user in the `files` bucket:
 *   New/templates/doc_templates/{sanitizedUserName}/{TYPECODE}.json
 *
 * The stored envelope mirrors a MedADocs document so the same Quill delta can
 * be re-applied directly:
 *   { delta: { ops }, sections, lastUpdated, _customTemplate }
 */

import { getSupabase } from '../../components/supabaseClient';
import { bucketName_files } from '../../components/env';
import { getCurrentUserName } from '../../utils/formProcessingUtils';

// Same sanitization the report-template code uses for storage path segments —
// keeps save and load resolving to identical folders.
const sanitizeSegment = (s) => String(s || '').replace(/[^a-zA-Z0-9@._-]/g, '_');

// A type code is short and case-insensitive: trim, uppercase, path-sanitize so
// "np", "NP" and " np " all resolve to the same template.
export function normalizeTypeCode(code) {
  return sanitizeSegment(String(code || '').trim().toUpperCase());
}

// Resolve the storage path for the current user + type code, or null if either
// is missing (no authenticated user / blank code).
async function templatePath(typeCode) {
  const code = normalizeTypeCode(typeCode);
  if (!code) return null;
  const userName = await getCurrentUserName();
  if (!userName) return null;
  return `New/templates/doc_templates/${sanitizeSegment(userName)}/${code}.json`;
}

// Save the given delta as the current user's template for `typeCode`.
// Returns the storage path on success; throws on failure.
export async function saveCustomTemplate({ typeCode, delta, sections = [] }) {
  const path = await templatePath(typeCode);
  if (!path) {
    throw new Error('Cannot save template — missing user name or type code.');
  }
  const ops = (delta && Array.isArray(delta.ops) && delta.ops) || [{ insert: '\n' }];
  const envelope = {
    delta: { ops },
    sections: Array.isArray(sections) ? sections : [],
    lastUpdated: new Date().toISOString(),
    _customTemplate: normalizeTypeCode(typeCode),
  };
  const supabase = await getSupabase();
  const blob = new Blob([JSON.stringify(envelope)], { type: 'application/json' });
  const { error } = await supabase.storage
    .from(bucketName_files)
    .upload(path, blob, { upsert: true });
  if (error) throw error;
  return path;
}

// Load the current user's template for `typeCode`. Returns { delta, sections }
// or null when none exists / the download or parse fails.
export async function loadCustomTemplate(typeCode) {
  const path = await templatePath(typeCode);
  if (!path) return null;
  try {
    const supabase = await getSupabase();
    const { data: blob, error } = await supabase.storage
      .from(bucketName_files)
      .download(path);
    if (error || !blob) return null;
    const parsed = JSON.parse(await blob.text());
    const ops = parsed?.delta?.ops;
    if (!Array.isArray(ops) || ops.length === 0) return null;
    return {
      delta: { ops },
      sections: Array.isArray(parsed.sections) ? parsed.sections : [],
    };
  } catch (_) {
    return null;
  }
}

// List the current user's saved template codes (filenames minus `.json`),
// uppercased. Reuses the same folder logic as save/load so all three can never
// resolve to different folders.
//
// Never throws and never returns null: the template dropdown must still offer
// the built-ins when a custom lookup fails, and this call can fail routinely —
// no signed-in user, a storage hiccup, or a hung auth session (getCurrentUserName
// reaches supabase.auth.getSession(), which can deadlock when multiple
// GoTrueClient instances share a storage key, as they do in this app).
export async function listCustomTemplates() {
  try {
    const userName = await getCurrentUserName();
    if (!userName) return [];
    const folder = `New/templates/doc_templates/${sanitizeSegment(userName)}`;
    const supabase = await getSupabase();
    const { data, error } = await supabase.storage
      .from(bucketName_files)
      .list(folder, { limit: 200 });
    if (error || !Array.isArray(data)) return [];
    return data
      .map((f) => String(f?.name || ''))
      .filter((n) => n.toLowerCase().endsWith('.json'))
      .map((n) => n.slice(0, -'.json'.length).trim().toUpperCase())
      .filter(Boolean);
  } catch (_) {
    return [];
  }
}
